import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
  Grid,
  Paper,
  Chip,
  makeStyles,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@material-ui/icons/Check';
import DeleteIcon from '@material-ui/icons/Delete';
import BuildIcon from '@material-ui/icons/Build';
import { AnalysisReviewSkeleton } from '../Skeletons';
import { Requirement } from './types';
import { useSharedStyles } from './styles/shared';
import { useCategoryLabel } from './utils/useCategoryLabel';
import { severityChipClass } from './utils/severity';

const useStyles = makeStyles(theme => ({
  scorePaper: {
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  scoreValuePrimary: {
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  scoreValueWarning: {
    fontWeight: 700,
    color: theme.palette.warning.main,
  },
  scoreValueSuccess: {
    fontWeight: 700,
    color: theme.palette.success.main,
  },
  requirementsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  requirementItem: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  requirementItemSelected: {
    backgroundColor: theme.palette.action.selected,
    borderColor: theme.palette.primary.main,
  },
  requirementItemDefault: {
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.palette.divider,
  },
  requirementMain: {
    flex: 1,
    overflow: 'hidden',
  },
  requirementId: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  requirementText: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  requirementActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  scoreChip: {
    fontWeight: 700,
  },
  scoreChipSuccess: {
    fontWeight: 700,
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  scoreChipWarning: {
    fontWeight: 700,
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
  },
  scoreChipError: {
    fontWeight: 700,
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  detailButton: {
    minWidth: '32px',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    fontWeight: 700,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
  },
  placeholderText: {
    fontStyle: 'italic',
  },
  ruleId: {
    fontWeight: 600,
  },
  actionButtons: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
  },
}));

interface AnalysisReviewProps {
  requirements: Requirement[];
  currentReqId: string | null;
  onSelectRequirement: (id: string) => void;
  onApprove: (id: string) => void;
  /** Reverse an approval (APPROVED → WIP). Only shown for approved requirements. */
  onUnapprove?: (id: string) => void;
  onDelete: (id: string) => void;
  onFixRequired: (id: string) => void;
  /** The role may fix (collaborator and above). */
  canWrite: boolean;
  /** The role may approve, un-approve and delete (maintainer and above). */
  canApprove: boolean;

  onBack?: () => void;
  loading?: boolean;
}

const AnalysisReview: React.FC<AnalysisReviewProps> = ({
  requirements,
  currentReqId,
  onSelectRequirement,
  onApprove,
  onUnapprove,
  onDelete,
  onFixRequired,
  canWrite,
  canApprove,

  onBack,
  loading = false,
}) => {
  const classes = useStyles();
  const shared = useSharedStyles();
  const { t } = useTranslation('requirement-doctor');
  const categoryLabel = useCategoryLabel();
  if (loading) {
    return <AnalysisReviewSkeleton />;
  }
  const current = requirements.find(r => r.id === currentReqId);

  // What the broken-rules card says when it has no rules to list. "No broken
  // rules" is a result, so it is only claimed for a requirement analysed here;
  // one analysed in an earlier session has scores but no rules to show.
  let emptyViolationsText: string | null = null;
  if (current?.analysisFailure) {
    emptyViolationsText = t('s4.right.violations.notAnalysed');
  } else if (current?.analysis) {
    emptyViolationsText = t('s4.right.violations.noViolations');
  } else if (current) {
    emptyViolationsText = t('s4.right.violations.notShown');
  }

  // Maps an ASPICE score to its colour class (>=7 ok, >=5 warning, else error).
  const scoreChipClass = (score: number): string => {
    if (score >= 7) return classes.scoreChipSuccess;
    if (score >= 5) return classes.scoreChipWarning;
    return classes.scoreChipError;
  };

  const ScoreCard = ({
    label,
    value,
    max,
  }: {
    label: string;
    value: number;
    max: number;
  }) => (
    <Paper className={classes.scorePaper}>
      <Typography variant="h4" className={classes.scoreValuePrimary}>
        {value.toFixed(1)}
      </Typography>
      <Typography variant="caption" color="textSecondary">
        {label} (0-{max})
      </Typography>
    </Paper>
  );

  return (
    <Grid container spacing={2}>
      {/* Back to the requirement selection screen (S1). */}
      {onBack && (
        <Grid item xs={12}>
          <Button variant="outlined" size="small" onClick={onBack}>
            {t('s4.buttons.back')}
          </Button>
        </Grid>
      )}

      {/* LEFT: Requirements List */}
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" className={shared.sectionTitle}>
              {t('s4.left.title')}
            </Typography>
            <Box className={classes.requirementsList}>
              {requirements.map(req => (
                <Box
                  key={req.id}
                  onClick={() => onSelectRequirement(req.id)}
                  className={`${classes.requirementItem} ${
                    currentReqId === req.id
                      ? classes.requirementItemSelected
                      : classes.requirementItemDefault
                  }`}
                >
                  <Box className={classes.requirementMain}>
                    <Typography
                      variant="body2"
                      className={classes.requirementId}
                    >
                      {req.id}
                    </Typography>
                    <Typography
                      variant="caption"
                      className={classes.requirementText}
                    >
                      {req.text}
                    </Typography>
                  </Box>
                  <Box className={classes.requirementActions}>
                    {req.status === 'APPROVED' && (
                      <Chip
                        label={t('s1.statuses.approved')}
                        size="small"
                        className={classes.scoreChipSuccess}
                      />
                    )}
                    {req.analysis && req.status !== 'APPROVED' && (
                      <Chip
                        label={req.analysis.aspiceScore.toFixed(1)}
                        size="small"
                        className={scoreChipClass(req.analysis.aspiceScore)}
                      />
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      className={classes.detailButton}
                      onClick={e => {
                        e.stopPropagation();
                        onSelectRequirement(req.id);
                      }}
                    >
                      ≫
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* RIGHT: Analysis Details */}
      <Grid item xs={12} md={8}>
        <Grid container spacing={2}>
          {/* Summary */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" className={shared.sectionTitle}>
                  {t('s4.right.summary.title')}
                </Typography>
                {current?.analysisFailure && (
                  <Alert severity="error">
                    <AlertTitle>{t('s4.right.summary.failedTitle')}</AlertTitle>
                    {current.analysisFailure.message && (
                      <Typography variant="body2">
                        {current.analysisFailure.message}
                      </Typography>
                    )}
                    <Typography variant="caption">
                      {t('s4.right.summary.failedHint')}
                    </Typography>
                  </Alert>
                )}
                {current && current.analysis ? (
                  <Grid container spacing={1}>
                    <Grid item xs={6} sm={3}>
                      <ScoreCard
                        label={t('common.aspiceScore')}
                        value={current.analysis.aspiceScore}
                        max={10}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <ScoreCard
                        label={t('common.incoseScore')}
                        value={current.analysis.incoseScore}
                        max={10}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper className={classes.scorePaper}>
                        <Typography
                          variant="h4"
                          className={classes.scoreValueWarning}
                        >
                          {current.analysis.violations}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {t('common.violations')}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper className={classes.scorePaper}>
                        <Typography
                          variant="h6"
                          className={classes.scoreValueSuccess}
                        >
                          {categoryLabel(current.analysis.category)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {t('common.category')}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                ) : (
                  // A failed analysis has no results; the alert above replaces
                  // the "pick a requirement" hint.
                  !current?.analysisFailure && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      className={classes.placeholderText}
                    >
                      {t('s4.right.summary.placeholder')}
                    </Typography>
                  )
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Broken Rules */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" className={shared.sectionTitle}>
                  {t('s4.right.violations.title')}
                </Typography>
                {current?.analysis?.brokenRules &&
                current.analysis.brokenRules.length > 0 ? (
                  <Box className={shared.tableWrapper}>
                    <Table size="small">
                      <TableHead className={shared.tableHead}>
                        <TableRow>
                          <TableCell>
                            {t('s4.right.violations.columns.ruleId')}
                          </TableCell>
                          <TableCell>
                            {t('s4.right.violations.columns.severity')}
                          </TableCell>
                          <TableCell>
                            {t('s4.right.violations.columns.violation')}
                          </TableCell>
                          <TableCell>
                            {t('s4.right.violations.columns.fix')}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {current.analysis.brokenRules.map(rule => (
                          <TableRow key={rule.rule}>
                            <TableCell>
                              <Typography
                                variant="body2"
                                className={classes.ruleId}
                              >
                                {rule.rule}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={rule.severity.toUpperCase()}
                                className={severityChipClass(
                                  rule.severity,
                                  shared,
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {rule.violation}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {rule.fix}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                ) : (
                  emptyViolationsText && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      className={classes.placeholderText}
                    >
                      {emptyViolationsText}
                    </Typography>
                  )
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Action Buttons */}
          {current && (
            <Grid item xs={12}>
              <Box className={classes.actionButtons}>
                {canApprove &&
                  (current.status !== 'APPROVED' ? (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<CheckIcon />}
                      onClick={() => onApprove(current.id)}
                      disabled={!current.analysis}
                      title={
                        !current.analysis
                          ? t('s4.approveDisabledHint')
                          : undefined
                      }
                    >
                      {t('s4.buttons.approve')}
                    </Button>
                  ) : (
                    onUnapprove && (
                      <Button
                        variant="outlined"
                        onClick={() => onUnapprove(current.id)}
                      >
                        {t('s4.buttons.unapprove')}
                      </Button>
                    )
                  ))}
                {/* An approved thread is terminal: the backend refuses edits and
                    deletes on it (409 thread-not-editable), and the spec has these
                    hidden rather than failing. Un-approve first to edit again. */}
                {current.status !== 'APPROVED' && (
                  <>
                    {canApprove && (
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<DeleteIcon />}
                        onClick={() => onDelete(current.id)}
                      >
                        {t('s4.buttons.delete')}
                      </Button>
                    )}
                    {/* Fixing needs suggestions to apply: the backend answers
                        400 analysis-not-run when a requirement has never been
                        analysed, and the screen has no way back out of that
                        error. Mirror the approve button and disable it. */}
                    {canWrite && (
                      <Button
                        variant="contained"
                        startIcon={<BuildIcon />}
                        onClick={() => onFixRequired(current.id)}
                        disabled={!current.analysis}
                        title={
                          !current.analysis
                            ? t('s4.fixDisabledHint')
                            : undefined
                        }
                      >
                        {t('s4.buttons.fixRequired')}
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AnalysisReview;
